import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import Cookies from 'universal-cookie';
import Link from 'next/link';
import classNames from 'classnames';
import { routes } from 'env/routes';
import { withRouter } from 'next/router';
import { slugify } from 'env/utils/strings';
import ActiveLink from 'ui/components/ActiveLink';
import withCustomComponent from 'ui/components/withCustomComponent';
import { clientConfig } from 'env/config';
import { useQuery } from '@tanstack/react-query';
import { eventsQueryConfig } from '../../env/hooks/useEventsQuery';
import { NAVIGATION_STATE_COOKIE_NAME } from '../../env/stores/appStore';
import { useMeHasFlagQuery } from '../hooks/meQuery';
import {
  homeHasTreatmentRooms,
  isTreatmentRoomsGroup,
} from 'env/utils/treatmentRoomsGroup';

const cookies = new Cookies();

const envconfig = clientConfig();
export const MainMenu = withCustomComponent('MainMenu')(
  inject(
    'contentStore',
    'coursesStore',
    'floorPlanStore',
    'invoicingStore',
    'appStore',
    'authStore',
    'bookingsStore',
    'homeStore',
    'eventsStore'
  )(
    withRouter(
      observer(
        ({
          componentName,
          router,
          appStore,
          contentStore,
          coursesStore,
          floorPlanStore,
          invoicingStore,
          bookingsStore,
          authStore,
          homeStore,
          pageProps,
        }) => {
          const { t } = useTranslation();
          // Tracks the Bookings disclosure dropdown's open/closed state so the
          // toggle button can expose an honest aria-expanded. Kept in sync with
          // Bootstrap's own shown/hidden events (see useEffect below).
          const [isBookingsDropdownOpen, setIsBookingsDropdownOpen] =
            React.useState(false);
          const {
            business,
            businesses,
            mainMenuDocked,
            corporateDashboard,
            virtualDashboard,
            mainMenuExpanded,
            publicCustomPages,
          } = appStore;
          const { isLoggedIn, customer, accessToken, host, agent } = authStore;
          const { faqArticles, communityPerks } = contentStore;
          const config = appStore.configuration;
          const homeConfig = homeStore.configuration;

          const enhancedUserQuery = useMeHasFlagQuery(
            agent,
            customer?.Email,
            'enhanced'
          );
          const showEnhancedLink = enhancedUserQuery.data;

          const gymClassesQuery = useQuery({
            meta: { gymEventId: envconfig.gymEventId },
            ...eventsQueryConfig(accessToken, host, 1, false),
            select: (data) => {
              return data?.CalendarEvents
                ? data.CalendarEvents.filter((event) =>
                    event.EventCategories.some(
                      (category) => category.Id === envconfig.gymEventId
                    )
                  )
                : [];
            },
          });

          const isCoursesRoute =
            router.pathname === routes.courses ||
            router.pathname === routes.my_courses;
          const coursesExist =
            coursesStore.coursesPage?.Courses?.length > 0 ||
            coursesStore.myCourses?.length > 0;

          const isTeamActive = [
            routes.team_dashboard,
            routes.team_attendance,
          ].includes(router.pathname);

          const isBookingsActive =
            router.pathname === routes.resources ||
            router.pathname === routes.my_bookings ||
            router.pathname === routes.floorplan ||
            router.pathname === routes.floorplan_by_id('[floorplan_id]') ||
            router.pathname === routes.calendar ||
            router.pathname === routes.my_events ||
            router.pathname === routes.visitors ||
            router.pathname === routes.deliveries;

          const isCommunityActive =
            router.pathname === routes.forum ||
            router.pathname === routes.forum_by_group('[group_id]') ||
            router.pathname === routes.forum_start ||
            router.pathname === routes.my_threads ||
            router.pathname === routes.private_threads ||
            router.pathname === routes.virtual_rooms ||
            router.pathname === routes.posts ||
            router.pathname === routes.events ||
            router.pathname === routes.event_by_id('[event_id]') ||
            router.pathname === routes.events_calendar ||
            router.pathname === routes.courses ||
            router.pathname === routes.my_courses ||
            router.pathname === routes.course_by_id('[course_id]') ||
            router.pathname === routes.lesson_by_id('[lesson_id]') ||
            router.pathname === routes.directory_members ||
            router.pathname === routes.directory_member_by_id('[member_id]') ||
            router.pathname === routes.directory_team_by_id('[team_id]') ||
            router.pathname === routes.directory_teams ||
            router.pathname === routes.custom_page('[...page_slug]') ||
            router.pathname === routes.gym_classes;

          const isSupportActive =
            router.pathname === routes.faq ||
            router.pathname === routes.support ||
            router.pathname === routes.support_new ||
            router.pathname === routes.support_message('[message_id]');

          const isMyDealsActive =
            router.pathname === routes.mydeals_deals ||
            router.pathname === routes.mydeals_finance;

          const isSettingsActive =
            router.pathname === routes.profile ||
            router.pathname === routes.profile_team ||
            router.pathname === routes.profile_team ||
            router.pathname === routes.profile_team ||
            router.pathname === routes.plan ||
            router.pathname === routes.invoices ||
            router.pathname === routes.integrations ||
            router.pathname === routes.notifications ||
            router.pathname === routes.files ||
            router.pathname === routes.metrics;

          const isHomeDashboard = router.pathname === routes.home;

          const canAccessHelpDesk =
            config['Access.Help'] == 2 ||
            (customer?.IsMember && config['Access.Help'] == 3) ||
            (!customer?.IsMember && config['Access.Help'] == 4);

          const initToolTips = () => {
            const $ = window.$;
            $('[data-toggle="tooltip"]').tooltip('dispose');
            $('[data-toggle="tooltip"]').tooltip({
              html: true,
            });
          };

          const updateDimensions = () => {
            if (window.innerWidth < 1024) {
              if (appStore.mainMenuDocked) {
                appStore.toggleMainMenuDocked(false);
                appStore.toggleMainMenuExpanded(false);
              }
            }
          };

          const openDropdown = (shouldHide) => {
            if (isTeamActive) {
              $('#dashboard-dropdown').off('hide.bs.dropdown');
              $('#dashboard-dropdown-toggle').dropdown('toggle');

              if (mainMenuExpanded) {
                // if menu is expanded, prevent dropdown from closing
                $('#dashboard-dropdown').on('hide.bs.dropdown', function (e) {
                  e.preventDefault();
                });
              }
            } else if (isBookingsActive) {
              $('#bookings-dropdown').off('hide.bs.dropdown');
              $('#bookings-dropdown-toggle').dropdown('toggle');

              if (mainMenuExpanded) {
                $('#bookings-dropdown').on('hide.bs.dropdown', function (e) {
                  e.preventDefault();
                });
              }
            } else if (isCommunityActive) {
              $('#community-dropdown').off('hide.bs.dropdown');
              $('#community-dropdown-toggle').dropdown('toggle');

              if (mainMenuExpanded) {
                $('#community-dropdown').on('hide.bs.dropdown', function (e) {
                  e.preventDefault();
                });
              }
            } else if (isSupportActive) {
              $('#support-dropdown').off('hide.bs.dropdown');
              $('#support-dropdown-toggle').dropdown('toggle');

              if (mainMenuExpanded) {
                $('#support-dropdown').on('hide.bs.dropdown', function (e) {
                  e.preventDefault();
                });
              }
            } else if (isMyDealsActive) {
              $('#mydeals-dropdown').off('hide.bs.dropdown');
              $('#mydeals-dropdown-toggle').dropdown('toggle');

              if (mainMenuExpanded) {
                $('#mydeals-dropdown').on('hide.bs.dropdown', function (e) {
                  e.preventDefault();
                });
              }
            } else if (isSettingsActive) {
              $('#settings-dropdown').off('hide.bs.dropdown');
              $('#settings-dropdown-toggle').dropdown('toggle');

              if (mainMenuExpanded) {
                $('#settings-dropdown').on('hide.bs.dropdown', function (e) {
                  e.preventDefault();
                });
              }
            }
          };

          const communityMenu = (config['PublicWebSite.CommunityBoard'] ||
            config['Jitsi.AlwaysOnRooms.Enabled'] ||
            config['Jitsi.AlwaysOnRooms.Enabled'] ||
            config['Blog.Enabled'] ||
            publicCustomPages.length > 0 ||
            (!corporateDashboard && config['PublicWebSite.Events']) ||
            coursesStore.coursesPage?.Courses?.length > 0 ||
            config['PublicWebSite.Directory']) && (
            <div
              className={`dropdown ${virtualDashboard ? 'show' : ''}`}
              id="community-dropdown"
            >
              <button
                data-toggle="dropdown"
                id="community-dropdown-toggle"
                className={isCommunityActive ? 'active' : ''}
              >
                <i
                  className="icon-community"
                  data-toggle="tooltip"
                  title={t('Community')}
                ></i>
                <span>{t('Community')}</span>
              </button>
              <div className="dropdown-menu">
                {homeConfig['PublicWebSite.CommunityBoard'] && (
                  <ActiveLink
                    href={routes.forum}
                    activeIfChildRoute
                    legacyBehavior
                  >
                    <a>{t('Notice Boards')}</a>
                  </ActiveLink>
                )}
                {config['Jitsi.AlwaysOnRooms.Enabled'] && (
                  <ActiveLink href={routes.virtual_rooms} legacyBehavior>
                    <a>{t('Virtual rooms')}</a>
                  </ActiveLink>
                )}
                {config['Blog.Enabled'] && (
                  <ActiveLink
                    href={routes.posts}
                    activeIfChildRoute
                    legacyBehavior
                  >
                    <a>{t('Articles')}</a>
                  </ActiveLink>
                )}
                {!corporateDashboard && config['PublicWebSite.Events'] && (
                  <ActiveLink
                    href={routes.events}
                    activeIfChildRoute
                    legacyBehavior
                  >
                    <a>{t('Events')}</a>
                  </ActiveLink>
                )}
                {(coursesExist || (isCoursesRoute && router.query?.query)) && (
                  <ActiveLink
                    href={routes.courses}
                    activeIfChildRoute
                    legacyBehavior
                  >
                    <a>{t('Courses')}</a>
                  </ActiveLink>
                )}
                {!corporateDashboard &&
                  config['PublicWebSite.Events'] &&
                  gymClassesQuery.data?.length > 0 && (
                    <ActiveLink
                      href={routes.gym_classes}
                      activeIfChildRoute
                      legacyBehavior
                    >
                      <a>{t('Gym Classes')}</a>
                    </ActiveLink>
                  )}
                {config['PublicWebSite.Directory'] && (
                  <ActiveLink
                    href={
                      config['Directory.DirectoryRecords'] == 2
                        ? routes.directory_teams
                        : routes.directory_members
                    }
                    activeIfChildRoute
                    legacyBehavior
                  >
                    <a>
                      {config['Directory.DirectoryRecords'] == 2
                        ? t('Teams')
                        : t('Members')}
                    </a>
                  </ActiveLink>
                )}

                {publicCustomPages.map((page, i) => (
                  <ActiveLink
                    href={routes.custom_page(page.Permalink)}
                    key={`customPage-${page.Id}-${page.Title || page.Name}`}
                    legacyBehavior
                  >
                    <a>{t(page.Title || page.Name)}</a>
                  </ActiveLink>
                ))}
              </div>
            </div>
          );

          useEffect(() => {
            initToolTips();
            updateDimensions();

            if (!appStore.hasLoadedReferralDiscounts)
              appStore.loadReferralDiscounts().catch((ex) => null);
            if (!contentStore.hasLoadedFaqArticles)
              contentStore.loadFaqArticles().catch((ex) => null);
            if (!contentStore.hasLoadedCommunityPerks)
              contentStore.loadCommunityPerks().catch((ex) => null);
            if (!bookingsStore.hasLoadedResources)
              bookingsStore.loadResources().catch(() => null);
            if (!bookingsStore.hasLoadedSensors)
              bookingsStore.loadSensors().catch(() => null);

            if (!coursesStore.hasLoadedCoursesPage)
              coursesStore
                .loadCoursesPage({ ...router.query })
                .catch((ex) => null);

            if (!invoicingStore.hasLoadedStoreProducts)
              invoicingStore
                .loadStoreProducts({ ...router.query })
                .catch((ex) => null);

            if (!appStore.mainMenuDocked)
              appStore.toggleMainMenuExpanded(false);
            if (appStore.mainMenuDocked) openDropdown();
            if (!appStore.hasLoadedBusinesses || !businesses.length)
              appStore.loadBusinesses().catch((ex) => null);

            window.addEventListener('resize', updateDimensions);

            // Get menu dock status from state cookie or default it based
            // on window width
            let defaultDockedValue = window.innerWidth > 1024;
            if (cookies.get(NAVIGATION_STATE_COOKIE_NAME))
              defaultDockedValue =
                cookies.get(NAVIGATION_STATE_COOKIE_NAME) == 'true';

            appStore.toggleMainMenuDocked(defaultDockedValue);
            appStore.toggleMainMenuExpanded(defaultDockedValue);

            window.updateSignalRConnectionStatus = (status) => {
              console.log('SIGNALR: connection status changed to', status);
              appStore.updateConnectionStatus(status);
            };
          }, []);

          useEffect(() => {
            initToolTips();
          });

          // Keep aria-expanded on the Bookings disclosure button in sync with
          // the actual Bootstrap dropdown state. Delegated off `document` so it
          // works regardless of when #bookings-dropdown mounts (it renders only
          // after bookingsStore.resources loads). Uses the past-tense
          // shown/hidden events so a prevented hide (openDropdown keeps it open
          // when the menu is expanded) does not report a stale collapsed state.
          useEffect(() => {
            const $ = window.$;
            if (!$) return;
            const onShown = () => setIsBookingsDropdownOpen(true);
            const onHidden = () => setIsBookingsDropdownOpen(false);
            $(document).on('shown.bs.dropdown', '#bookings-dropdown', onShown);
            $(document).on('hidden.bs.dropdown', '#bookings-dropdown', onHidden);
            return () => {
              $(document).off(
                'shown.bs.dropdown',
                '#bookings-dropdown',
                onShown
              );
              $(document).off(
                'hidden.bs.dropdown',
                '#bookings-dropdown',
                onHidden
              );
            };
          }, []);

          const customBusinessId = envconfig.renoldBusinessId;
          const customBusiness = businesses.find(
            (b) => b.Id == customBusinessId
          );
          const isCustomBusiness = customer?.HomeSpaceId == customBusinessId;

          if (!business) return <></>;
          if (!isLoggedIn) return <></>;

          const { resourceGroups, resources } = bookingsStore;
          const homeBusinessId = customer?.InvoicingBusinessId;
          const hasTreatmentRooms = homeHasTreatmentRooms(
            homeBusinessId,
            resources,
          );
          const filteredResourceGroups = resourceGroups.filter(
            (g) => !isTreatmentRoomsGroup(g.groupName) || hasTreatmentRooms,
          );
          // move 'All Resources' group to first element
          if (
            filteredResourceGroups.length &&
            filteredResourceGroups[filteredResourceGroups.length - 1]
              .groupName === null
          ) {
            const allResourcesGroup = filteredResourceGroups.pop();
            filteredResourceGroups.unshift(allResourcesGroup);
          }

          const inMsTeams = cookies.get('in-ms-teams') == 'true';

          return (
            <>
              <>
                {inMsTeams && location.pathname != '/bookings/search' && (
                  <div class="d-none ml-10 mr-4 float-left mt-10 menu-less-button">
                    <Link
                      href="/bookings/search"
                      class="btn btn-single-icon btn-white"
                    >
                      <i class="icon-go-back"></i>
                    </Link>
                  </div>
                )}
              </>
              <div
                onClick={() => appStore.toggleMainMenuExpanded()}
                className="site-mainmenu__shadow"
              ></div>
              <aside
                data-component-name={componentName}
                className={classNames('site-mainmenu', {
                  'site-mainmenu--expanded': mainMenuExpanded,
                  'site-mainmenu--contracted': !mainMenuExpanded,
                  'site-mainmenu--docked': mainMenuExpanded && mainMenuDocked,
                  'site-mainmenu--home': isHomeDashboard,
                })}
              >
                <div className="site-mainmenu__content">
                  <header className="site-mainmenu__header">
                    <button
                      onClick={() => appStore.toggleMainMenuExpanded(false)}
                      className="toggle-mobile"
                    >
                      <i className="icon-close-large"></i>
                    </button>

                    <ActiveLink href="/" legacyBehavior>
                      <a className="brand">
                        <img
                          src={`${
                            isCustomBusiness
                              ? customBusiness?.NativeHomeUrlWithLanguage
                              : business?.NativeHomeUrlWithLanguage
                          }/business/getlogo?h=192&mode=pad`}
                          alt={t(business.Name)}
                          className={
                            isCustomBusiness ? 'custom-sidebar-logo' : ''
                          }
                        />
                      </a>
                    </ActiveLink>

                    <button
                      onClick={() => {
                        if (mainMenuExpanded) {
                          appStore.toggleMainMenuDocked();
                          openDropdown();
                        } else {
                          appStore.toggleMainMenuExpanded();
                          openDropdown();
                        }
                      }}
                      className="toggle-desktop"
                    >
                      <i
                        className={`icon-${
                          mainMenuExpanded
                            ? mainMenuDocked
                              ? 'close-sidebar'
                              : 'lock'
                            : 'expand-sidebar'
                        }`}
                        data-toggle="tooltip"
                        title={
                          mainMenuExpanded
                            ? mainMenuDocked
                              ? t('Undock navigation')
                              : t('Dock navigation')
                            : t('Toggle navigation')
                        }
                      ></i>
                    </button>
                  </header>

                  <nav className="site-mainmenu__nav">
                    {/* Location selector */}
                    {/* <LocationsMenu
                    showCurrent={true}
                    btnClassName={' '}
                    hideCaret={true}
                  /> */}

                    {/* Dashboard */}
                    <ActiveLink href={routes.home}>
                      <button>
                        <i
                          className="icon-dashboard"
                          data-toggle="tooltip"
                          title={
                            virtualDashboard
                              ? t('Community Feed')
                              : t('My dashboard')
                          }
                          onClick={initToolTips}
                        ></i>
                        <span>
                          {virtualDashboard
                            ? t('Community Feed')
                            : t('My dashboard')}
                        </span>
                      </button>
                    </ActiveLink>
                    {bookingsStore.sensors?.length > 0 && (
                      <ActiveLink activeIfChildRoute href={routes.sensors}>
                        <button>
                          <i
                            className="icon-business-spaces"
                            data-toggle="tooltip"
                            title={t('My building')}
                            onClick={initToolTips}
                          ></i>
                          <span>{t('My building')}</span>
                        </button>
                      </ActiveLink>
                    )}
                    {/* Team */}
                    {/* {customer?.TeamNames && (
                    <div className="dropdown" id="dashboard-dropdown">
                      <button
                        data-toggle="dropdown"
                        id="dashboard-dropdown-toggle"
                        className={classNames({
                          active: isTeamActive,
                        })}
                      >
                        <i
                          className="icon-team"
                          data-toggle="tooltip"
                          title={t('Team')}
                        ></i>

                        <span>{t('Team')}</span>
                      </button>

                      <div className="dropdown-menu">
                        <ActiveLink href={routes.team_dashboard}>
                          <a>
                            {virtualDashboard
                              ? t('Community Feed')
                              : t('Dashboard')}
                          </a>
                        </ActiveLink>

                        <ActiveLink
                          id="my-team-dashboard-link"
                          href={routes.team_attendance}
                        >
                          <a>{t('My Team')}</a>
                        </ActiveLink>
                      </div>
                    </div>
                  )} */}

                    {virtualDashboard && communityMenu}

                    {/* Bookings */}
                    {bookingsStore.resources?.length > 0 && (
                      <div className="dropdown" id="bookings-dropdown">
                        <button
                          data-toggle="dropdown"
                          id="bookings-dropdown-toggle"
                          className={isBookingsActive ? 'active' : ''}
                          aria-label={t('Bookings')}
                          aria-haspopup="true"
                          aria-expanded={isBookingsDropdownOpen}
                          aria-controls="bookings-dropdown-menu"
                        >
                          <i
                            className="icon-bookings"
                            data-toggle="tooltip"
                            title={t('Bookings')}
                          ></i>
                          <span>{t('Bookings')}</span>
                        </button>
                        <div
                          className="dropdown-menu"
                          id="bookings-dropdown-menu"
                        >
                          
                            <ActiveLink
                              href={routes.my_bookings}
                              legacyBehavior
                            >
                              <a id={`my-bookings`}>
                                My Bookings
                              </a>
                            </ActiveLink>
                          
                          {filteredResourceGroups.length == 1 && (
                            <ActiveLink
                              href={routes.resources}
                              activeIfChildRoute
                              legacyBehavior
                            >
                              <a id="all_resources_link">
                                {t('Available resources')}
                              </a>
                            </ActiveLink>
                          )}
                          {filteredResourceGroups.length > 1 &&
                            filteredResourceGroups.map((group) => (
                              
                                <ActiveLink
                                  href={`${
                                    routes.resources
                                  }?group=${encodeURIComponent(
                                    group.groupName ?? ''
                                  )}`}
                                  legacyBehavior
                                >
                                  <a
                                    id={`resource_group_link_${slugify(
                                      group.groupName
                                    )}`}
                                  >
                                    {group.groupName?.length > 0
                                      ? t(group.groupName)
                                      : t('All Resources')}
                                  </a>
                                </ActiveLink>
                             
                            ))}
                          {/* <ActiveLink href={routes.calendar} activeIfChildRoute>
                          <a id="calendar_link">{t('Calendar')}</a>
                        </ActiveLink> */}
                          {!virtualDashboard &&
                            floorPlanStore.floorPlans.length > 0 && (
                              <ActiveLink
                                id="floorplan_link"
                                href={routes.floorplan}
                                activeIfChildRoute
                                legacyBehavior
                              >
                                <a>{t('Floor plan')}</a>
                              </ActiveLink>
                            )}
                          <ActiveLink
                            href={routes.resources_map}
                            activeIfChildRoute
                            legacyBehavior
                          >
                            <a id="map_link">{t('Map')}</a>
                          </ActiveLink>
                        </div>
                      </div>
                    )}
                    {/* My Deals */}
                    {!!showEnhancedLink && (
                      <div className="dropdown" id="mydeals-dropdown">
                        <button
                          data-toggle="dropdown"
                          id="mydeals-dropdown-toggle"
                          className={isMyDealsActive ? 'active' : ''}
                        >
                          <i
                            className="icon-user"
                            data-toggle="tooltip"
                            title={t('My Deals')}
                          ></i>
                          <span>{t('My Deals')}</span>
                        </button>
                        <div className="dropdown-menu">
                          <ActiveLink
                            href={routes.mydeals_deals}
                            legacyBehavior
                          >
                            <a>{t('Deals')}</a>
                          </ActiveLink>
                          <ActiveLink
                            href={routes.mydeals_finance}
                            legacyBehavior
                          >
                            <a>{t('Finance')}</a>
                          </ActiveLink>
                        </div>
                      </div>
                    )}
                    {/* Products and Services */}
                    {invoicingStore.storeProducts.products.length > 0 && (
                      <ActiveLink href={routes.store}>
                        <button>
                          <i
                            className="icon-products"
                            data-toggle="tooltip"
                            title={
                              !corporateDashboard
                                ? t('Products and services')
                                : t('Add-ons')
                            }
                            // onClick={initToolTips}
                          ></i>
                          {!corporateDashboard ? (
                            <span>{t('Products and services')}</span>
                          ) : (
                            <span>{t('Add-ons')}</span>
                          )}
                        </button>
                      </ActiveLink>
                    )}

                    {/* Community */}
                    {!virtualDashboard && communityMenu}

                    {/* Perks Benefits */}
                    {communityPerks.length > 0 && (
                      <ActiveLink href={routes.perks}>
                        <button>
                          <i
                            className="icon-perk-benefits"
                            data-toggle="tooltip"
                            title={t('Perks and benefits')}
                            onClick={initToolTips}
                          ></i>
                          <span>{t('Perks and benefits')}</span>
                        </button>
                      </ActiveLink>
                    )}

                    {/* Support */}
                    {(faqArticles.length > 0 ||
                      config['PublicWebSite.Help']) && (
                      <div className="dropdown" id="support-dropdown">
                        <button
                          data-toggle="dropdown"
                          id="support-dropdown-toggle"
                          className={isSupportActive ? 'active' : ''}
                        >
                          <i
                            className="icon-support"
                            data-toggle="tooltip"
                            title={t('Support')}
                          ></i>
                          <span>{t('Support')}</span>
                        </button>
                        <div className="dropdown-menu">
                          {faqArticles.length > 0 && (
                            <ActiveLink href={routes.faq} legacyBehavior>
                              <a>{t('FAQ')}</a>
                            </ActiveLink>
                          )}
                          {homeConfig['PublicWebSite.Help'] &&
                            canAccessHelpDesk && (
                              <ActiveLink
                                href={routes.support}
                                activeIfChildRoute
                                legacyBehavior
                              >
                                <a>{t('Help request')}</a>
                              </ActiveLink>
                            )}
                        </div>
                      </div>
                    )}

                    {/* Settings */}
                    <div className="dropdown" id="settings-dropdown">
                      <button
                        data-toggle="dropdown"
                        id="settings-dropdown-toggle"
                        className={isSettingsActive ? 'active' : ''}
                      >
                        <i
                          className="icon-settings"
                          data-toggle="tooltip"
                          title={t('Settings')}
                        ></i>
                        <span>{t('Settings')}</span>
                      </button>
                      <div className="dropdown-menu">
                        {config['PublicWebSite.MyAccount'] && (
                          <ActiveLink href={routes.profile} legacyBehavior>
                            <a>{t('My Account')}</a>
                          </ActiveLink>
                        )}
                        {/* {customer?.TeamNames && !customer?.IsTeamAdministrator && (
                        <ActiveLink
                          id="my-team-link"
                          href={routes.profile_team}
                        >
                          <a>{t('My team')}</a>
                        </ActiveLink>
                      )}
                      {customer?.IsTeamAdministrator && (
                        <ActiveLink href={routes.profile_team}>
                          <a>{t('Team Settings')}</a>
                        </ActiveLink>
                      )} */}
                        {!corporateDashboard && (
                          <ActiveLink href={routes.plan} legacyBehavior>
                            <a>{t('Plans and benefits')}</a>
                          </ActiveLink>
                        )}
                        {!corporateDashboard &&
                          config['Members.ViewInvoices'] &&
                          config['SignupForm.ShowBillingSection'] == 'true' && (
                            <ActiveLink href={routes.invoices} legacyBehavior>
                              <a>{t('Billing')}</a>
                            </ActiveLink>
                          )}
                        {/* <ActiveLink href={routes.integrations}>
                        <a>{t('Integrations')}</a>
                      </ActiveLink> */}
                        <ActiveLink href={routes.notifications} legacyBehavior>
                          <a>{t('Notifications')}</a>
                        </ActiveLink>
                        <ActiveLink href={routes.metrics} legacyBehavior>
                          <a>{t('Metrics')}</a>
                        </ActiveLink>
                      </div>
                    </div>
                  </nav>

                  <footer className="site-mainmenu__footer">
                    <div className="customer">
                      <img
                        src={`${business.NativeHomeUrlWithLanguage}/coworker/getavatar/${customer.Id}?t=${customer.UpdatedOn}&h=32&w=32&noavatar=%2Fcontent%2Fthemes%2Fpublic%2Fdos%2Fimg%2Favatar-default.png`}
                        id="Customer_Avatar_Img"
                        className="avatar"
                        alt={customer.FullName}
                      />
                      <span>{customer.FullName}</span>
                    </div>
                    <a href={routes.logout} className="logout">
                      <i className="icon-log-out"></i>
                    </a>
                  </footer>
                </div>
              </aside>
            </>
          );
        }
      )
    )
  )
);
